import type { ReloadSafety } from './reload-blockers';

export type NativeReloadSafetyReport = {
  documentId: string;
  revision: number;
  state: ReloadSafety;
};
export type NativeReloadSafetyBridge = {
  getDocumentId: () => Promise<string>;
  reportReloadSafety: (report: NativeReloadSafetyReport) => unknown;
};

/** Narrow the optional preload API without importing Electron into the web app. */
export function readNativeReloadSafetyBridge(
  value: unknown
): NativeReloadSafetyBridge | null {
  if (
    typeof value !== 'object' ||
    value === null ||
    !('getDocumentId' in value) ||
    !('reportReloadSafety' in value)
  )
    return null;
  const getDocumentId = value.getDocumentId;
  const reportReloadSafety = value.reportReloadSafety;
  if (
    typeof getDocumentId !== 'function' ||
    typeof reportReloadSafety !== 'function'
  )
    return null;
  return {
    getDocumentId: async () => {
      const id: unknown = await getDocumentId();
      if (typeof id !== 'string' || !id)
        throw new Error('DOCUMENT_ID_UNAVAILABLE');
      return id;
    },
    reportReloadSafety: (report) => reportReloadSafety(report),
  };
}

/** Main owns the nonce and rejects stale/foreign reports. Never supply a web nonce. */
export function startNativeReloadSafetyReporting(
  bridge: NativeReloadSafetyBridge,
  registry: {
    getNativeReloadSafety: () => ReloadSafety;
    subscribe: (listener: () => void) => () => void;
  }
) {
  let active = true;
  let suspended = false;
  let acquisition = 0;
  let documentId: string | null = null;
  let revision = 0;
  const report = (state: ReloadSafety) => {
    if (!active || !documentId) return;
    try {
      const result = bridge.reportReloadSafety({
        documentId,
        revision: ++revision,
        state,
      });
      void Promise.resolve(result).catch(() => undefined);
    } catch {
      /* The native side keeps unknown on missing delivery. */
    }
  };
  const sync = () => {
    if (!suspended) report(registry.getNativeReloadSafety());
  };
  const unsubscribe = registry.subscribe(sync);
  const acquire = () => {
    const request = ++acquisition;
    void bridge
      .getDocumentId()
      .then((id) => {
        if (!active || suspended || request !== acquisition) return;
        documentId = id;
        report('unknown');
        sync();
      })
      .catch(() => undefined);
  };
  acquire();
  const heartbeat = setInterval(sync, 15_000);
  return {
    suspend() {
      report('unknown');
      suspended = true;
      acquisition += 1;
    },
    resume() {
      suspended = false;
      acquire();
    },
    dispose() {
      report('unknown');
      active = false;
      unsubscribe();
      clearInterval(heartbeat);
    },
  };
}
